// Curators cancel an event by prefixing its title "(CANCELLED) " and keeping
// status Complete — Tina's refresh writes the same prefix — so the app still
// lists it. A leading mark is certain; "cancelled" anywhere else in a title is
// a guess, so those rows start unticked.
const LEADING_MARK = /^\s*[([]\s*cancell?ed\s*[)\]]/i;
const ANY_MARK = /cancell?ed/i;

export type CancelMark = "sure" | "maybe";

/** The `/events/explore` summary fields this feature reads. */
export interface CancelledEventDoc {
  id: string;
  name: string;
  startDate?: number | null;
  geodata?: { name?: string | null } | null;
  venue?: { name?: string | null } | null;
}

export interface CancelledCandidate {
  id: string;
  name: string;
  startDate: number | null;
  city: string | null;
  venue: string | null;
  mark: CancelMark;
  // An event with ticketing can hold paid orders; deleting it strands them,
  // so a curator deletes it by hand once the orders are settled.
  keptReason: "ticketing" | null;
}

export interface DeleteResult {
  deleted: string[];
  failed: { id: string; message: string }[];
}

export function cancelMark(name: string): CancelMark | null {
  if (LEADING_MARK.test(name)) return "sure";
  if (ANY_MARK.test(name)) return "maybe";
  return null;
}

const groupOrder = (c: CancelledCandidate) =>
  c.keptReason ? 2 : c.mark === "sure" ? 0 : 1;

export function toCandidates(
  docs: CancelledEventDoc[],
  ticketedIds: Set<string>,
): CancelledCandidate[] {
  const byId = new Map<string, CancelledCandidate>();
  for (const doc of docs) {
    const mark = cancelMark(doc.name ?? "");
    if (!mark || byId.has(doc.id)) continue;
    byId.set(doc.id, {
      id: doc.id,
      name: doc.name,
      startDate: doc.startDate ?? null,
      city: doc.geodata?.name ?? null,
      venue: doc.venue?.name ?? null,
      mark,
      keptReason: ticketedIds.has(doc.id) ? "ticketing" : null,
    });
  }
  return [...byId.values()].sort(
    (a, b) =>
      groupOrder(a) - groupOrder(b) ||
      (a.startDate ?? Infinity) - (b.startDate ?? Infinity),
  );
}

export function defaultSelection(
  candidates: CancelledCandidate[],
): Set<string> {
  return new Set(
    candidates
      .filter((c) => c.mark === "sure" && !c.keptReason)
      .map((c) => c.id),
  );
}

export async function searchAllPages<T>(
  fetchPage: (page: number) => Promise<{ found: number; hits: T[] }>,
  perPage: number,
): Promise<T[]> {
  const all: T[] = [];
  for (let page = 1; ; page++) {
    const { found, hits } = await fetchPage(page);
    all.push(...hits);
    if (hits.length === 0 || all.length >= found || hits.length < perPage)
      return all;
  }
}

/**
 * Deletes one id at a time per worker, `concurrency` workers wide. Every
 * delete fires the external-sync trigger (Typesense + Knock), and a burst makes
 * Knock answer 429, so this stays well below the ~100 docs/s where that began.
 */
export async function deleteAll(
  ids: string[],
  remove: (id: string) => Promise<unknown>,
  {
    concurrency = 5,
    onProgress,
  }: { concurrency?: number; onProgress?: (done: number) => void } = {},
): Promise<DeleteResult> {
  const result: DeleteResult = { deleted: [], failed: [] };
  const queue = [...ids];
  let done = 0;

  const worker = async () => {
    for (let id = queue.shift(); id !== undefined; id = queue.shift()) {
      try {
        await remove(id);
        result.deleted.push(id);
      } catch (error) {
        result.failed.push({
          id,
          message: error instanceof Error ? error.message : String(error),
        });
      }
      done += 1;
      onProgress?.(done);
    }
  };

  await Promise.all(
    Array.from({ length: Math.min(concurrency, ids.length) }, worker),
  );
  return result;
}
